<script setup lang="ts">
import type { CartItemType, ProductType } from "@/types";
import {
  BLOCK_AFFILIATE_ON_PRODUCT_PAGE,
  isAffiliateOnlyProduct,
} from "~/composables/useAffiliateProduct";
import { onMounted } from "vue";
import { viewItemEvent, addToCartEvent } from "~/utils/gtmUtils";

const commerceStore = useCommerceStore();
const activationStore = useActivationStore();
const mainStore = useMainStore();

const { getProducts } = useBackendService();
const { fetchStrapiProducts } = useStrapiProducts();
const { fetchProducts } = useProducts();
const { currency, getProductPrice } = formatterUtils();
const { 
  product: productTracking, 
  startTimer, 
  endTimer, 
  trackComponentError,
  track
} = usePostHogTracking('ProductPage');

const cardType = computed(
  () => mainStore?.globalSettings?.CardType || "Classic"
);

const route = useRoute();
const slug = ref<string>(route.params.slug.toString());
const product = ref<ProductType>();
// IGNITE-104: set when an affiliate product is requested through this page
const affiliateBlocked = ref(false);
const { totalItemsInCart } = storeToRefs(commerceStore);

const cartItem = ref<CartItemType>({
  product: {} as ProductType,
  quantity: 1,
});

const handlePosition = ref("0px");

const updateHandlePosition = () => {
  try {
    // if (!process.client || !document) return;

    if (typeof window !== "undefined") {
      nextTick(() => {
        // const handle = document.querySelector(
        //   ".vue3-slider .handle"
        // ) as HTMLElement;
        // if (handle) {
        //   handlePosition.value = handle.style.left;
        // }
      });
    }
  } catch (error) {
    console.error(error);
    trackComponentError('updateHandlePosition_failed', error as Error);
  }
};

watch(() => cartItem.value.quantity, updateHandlePosition);
console.log("watch", cartItem.value, updateHandlePosition);

// Initialize product data using the same pattern as home page
const getProduct = async () => {
  try {
    console.log("[Product Page] Loading products for slug:", slug.value);
    
    // Call getProducts() directly like the home page does
    const allProducts = await getProducts();
    console.log("[Product Page] Raw products received:", allProducts?.length || 0);
    
    // Debug: Log product structure
    if (allProducts && allProducts.length > 0) {
      console.log("[Product Page] Sample product structure:", allProducts[0]);
      console.log("[Product Page] All product IDs:", allProducts.map((p: ProductType) => ({ id: p.id, code: p.code, name: p.name })));
    }

    // Add defensive validation like home page
    if (!allProducts || !Array.isArray(allProducts)) {
      console.warn("[Product Page] Products API returned invalid data:", allProducts);
      throw createError({
        statusCode: 404,
        statusMessage: "Product not found"
      });
    }
    
    // Store products in commerce store like home page
    commerceStore.updateProducts(allProducts);
    console.log("[Product Page] Products stored in commerce store");
    
    // Extract product ID from slug (e.g., data-44 -> 44)
    let productId: number | null = null;
    const matches = slug.value.match(/(?:data|mobile|mifi)-(\d+)/);
    if (matches?.[1]) {
      productId = Number.parseInt(matches[1], 10);
      console.log("[Product Page] Extracted ID:", productId);
    }
    
    // Find the product from the loaded data
    let foundProduct: ProductType | undefined;
    if (productId) {
      // Try both number and string comparison for ID
      foundProduct = allProducts.find((p: ProductType) => 
        p.id === productId || String(p.id) === String(productId) || Number(p.id) === productId
      );
      console.log("[Product Page] Found by ID:", foundProduct?.name || 'NOT FOUND');
    }
    
    // Fallback to code-based lookup
    if (!foundProduct) {
      foundProduct = allProducts.find((p: ProductType) => p.code === slug.value);
      console.log("[Product Page] Found by code:", foundProduct?.name || 'NOT FOUND');
    }
    
    if (!foundProduct) {
      console.error("[Product Page] Product not found:", slug.value);
      throw createError({
        statusCode: 404,
        statusMessage: "Product not found"
      });
    }

    // IGNITE-104: affiliate products are only reachable through their tokenised
    // /affiliate/{token} URL. Serving them here too would leave the guessable
    // URL the ticket exists to close. Behind a constant pending confirmation.
    if (BLOCK_AFFILIATE_ON_PRODUCT_PAGE && isAffiliateOnlyProduct(foundProduct)) {
      console.warn(
        "[Product Page] Affiliate product blocked on the public product page:",
        foundProduct.code
      );
      affiliateBlocked.value = true;
      return;
    }

    // Set the product and update UI
    product.value = foundProduct;
    cartItem.value.product = foundProduct;
    
    useHead({
      title: `${foundProduct.name} | OnePass mobile`,
    });
    track('Product Viewed', { product: foundProduct });
    viewItemEvent(foundProduct, cartItem.value.quantity);

    console.log("[Product Page] Product loaded successfully:", foundProduct.name);
    
  } catch (error) {
    console.error("[Product Page] Error loading product:", error);
    throw error;
  }
};

// Load product data on mount like home page does
onMounted(() => {
  getProduct();
});

const updateQuantity = (action: string) => {
  if (action === "add") {
    cartItem.value.quantity++;
  } else {
    if (cartItem.value.quantity === 1) {
      return;
    }
    cartItem.value.quantity--;
  }
};

const addToCart = () => {
  if (!product.value || !cartItem.value.quantity) {
    return;
  }
  commerceStore.addToCart(cartItem.value);
  track('Add to Cart', { product: product.value, quantity: cartItem.value.quantity });
  addToCartEvent(product.value, cartItem.value.quantity);
  clearFields();
};

const buyNow = () => {
  addToCart();
  activationStore.clearActivationSteps();
  track('Buy Now', { product: product.value, quantity: cartItem.value.quantity });
  navigateTo("/checkout");
};

const clearFields = () => {
  cartItem.value = {
    product: product.value as ProductType,
    quantity: 1,
  };
};
</script>

<template>
  <ProductNotFound v-if="affiliateBlocked" />
  <ProductClassic
    v-else-if="product && cardType === 'Classic'"
    :product="product"
    :cartItem="cartItem"
    :handlePosition="handlePosition"
    :updateHandlePosition="updateHandlePosition"
    :totalItemsInCart="totalItemsInCart"
    :updateQuantity="updateQuantity"
    :addToCart="addToCart"
    :buyNow="buyNow"
    :clearFields="clearFields"
  />
  <ProductDetail
    v-if="product && cardType === 'Modern'"
    :product="product"
    :cartItem="cartItem"
    :handlePosition="handlePosition"
    :updateHandlePosition="updateHandlePosition"
    :totalItemsInCart="totalItemsInCart"
    :updateQuantity="updateQuantity"
    :addToCart="addToCart"
    :buyNow="buyNow"
    :clearFields="clearFields"
  />
</template>

<style scoped lang="scss">
.product-page {
  @apply container flex flex-col bg-background;

  .content {
    @apply flex flex-row justify-center max-md:flex-col;

    .product-detail {
      @apply flex-1 flex justify-center;
    }
  }
}
</style>
